import { useState } from 'react';
import { useField } from 'formik';
import { Field, formikHelpers } from '@round/creatorbase-ui';
import { TargetChip, TargetChips } from '../TargetChips/TargetChips';
import { TargetChipsInput } from '../TargetChips/TargetChipsInput';
import { isValidHashtag, normaliseHashtag } from '../../helpers';
import { HashtagChip } from './HashtagChip';

const { Field: FormikField } = formikHelpers;

const INPUT_LABEL = 'Hashtags';

/** What the endpoint takes. The input refuses the rest rather than letting the create be rejected. */
const MAX_HASHTAGS = 20;

const LIMIT_MESSAGE = `Only ${MAX_HASHTAGS} hashtags can be targeted.`;

type Props = {
    disabled?: boolean;
};

/**
 * A hashtag target is nothing but a string, so it needs no resolution: a tag is a chip the moment it
 * is typed. What the backend stores is lowercased and deduped again, and the modal leaves it at that.
 */
export const HashtagTargets = ({ disabled }: Props) => {
    const [{ value: hashtags }, , { setValue }] = useField<string[]>('hashtags');
    const [limitMessage, setLimitMessage] = useState<string | null>(null);

    const handleAdd = (identifiers: string[]) => {
        const { added, isLimitReached } = getAddableHashtags(hashtags, identifiers);

        setLimitMessage(isLimitReached ? LIMIT_MESSAGE : null);

        if (added.length) {
            setValue([...hashtags, ...added]);
        }
    };

    const handleRemove = (hashtag: string) => {
        setLimitMessage(null);
        setValue(hashtags.filter((value) => value !== hashtag));
    };

    return (
        <FormikField name="hashtags">
            <FormikField.Title>{INPUT_LABEL}</FormikField.Title>
            <TargetChipsInput
                label={INPUT_LABEL}
                placeholder="Type or paste tags, then press Enter"
                disabled={disabled}
                onAdd={handleAdd}
            />

            {limitMessage && <Field.Error>{limitMessage}</Field.Error>}
            <FormikField.Error name="hashtags" />

            <TargetChips chips={getChips(hashtags)} disabled={disabled} onRemove={handleRemove} />
        </FormikField>
    );
};

const getChips = (hashtags: string[]): TargetChip[] =>
    hashtags.map((hashtag) => ({ key: hashtag, name: hashtag, label: <HashtagChip hashtag={hashtag} /> }));

/**
 * Duplicates collapse case-insensitively, since the backend lowercases anyway, and the cap counts the
 * tags already held — so a paste that overruns it adds what fits and says the rest were refused.
 */
const getAddableHashtags = (current: string[], identifiers: string[]) => {
    const seen = new Set(current.map((hashtag) => hashtag.toLowerCase()));
    const added: string[] = [];
    let isLimitReached = false;

    identifiers.forEach((identifier) => {
        const hashtag = normaliseHashtag(identifier);
        if (!isValidHashtag(hashtag) || seen.has(hashtag.toLowerCase())) {
            return;
        }

        if (current.length + added.length >= MAX_HASHTAGS) {
            isLimitReached = true;
            return;
        }

        seen.add(hashtag.toLowerCase());
        added.push(hashtag);
    });

    return { added, isLimitReached };
};
